import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { User, Bot, Sparkles, ThumbsDown, ThumbsUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils';
import type { Message } from '@/types';
import { useLanguage } from '@/i18n/LanguageContext';

interface ChatMessageProps {
  message: Message;
  children?: React.ReactNode;
  onSuggestionClick?: (suggestion: string) => void;
  onFeedback?: (rating: 'helpful' | 'not_helpful') => Promise<void>;
}

function extractSuggestions(text: string): { body: string; suggestions: string[] } {
  const marker = /(?:^|\n)Suggested queries:\s*\n/i;
  const match = marker.exec(text);
  if (!match) {
    return { body: text, suggestions: [] };
  }

  const body = text.slice(0, match.index).trim();
  const rest = text.slice(match.index + match[0].length);
  const lines = rest.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const suggestions: string[] = [];

  for (const line of lines) {
    const bulletMatch = line.match(/^[-*]\s+(.*)$/) || line.match(/^\d+\.\s+(.*)$/);
    const value = (bulletMatch ? bulletMatch[1] : line).trim();
    if (value) {
      suggestions.push(value);
    }
  }

  return {
    body: body || text,
    suggestions,
  };
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  children,
  onSuggestionClick,
  onFeedback,
}) => {
  const { language, t } = useLanguage();
  const [feedbackRating, setFeedbackRating] = React.useState<'helpful' | 'not_helpful' | null>(null);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = React.useState(false);
  const isUser = message.sender === 'user';
  const { body, suggestions } = extractSuggestions(message.text);
  const clarificationOptions = message.data?.clarification_options ?? [];
  const showClarifications = !isUser && clarificationOptions.length > 0;
  const showSuggestions = !isUser && !showClarifications && suggestions.length > 0;

  const submitFeedback = async (rating: 'helpful' | 'not_helpful') => {
    if (!onFeedback || isSubmittingFeedback) return;
    setIsSubmittingFeedback(true);
    try {
      await onFeedback(rating);
      setFeedbackRating(rating);
    } catch {
      // Keep the controls available so the user can try again.
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <div className={cn(
      'flex gap-3 animate-slide-up',
      isUser ? 'flex-row-reverse' : 'flex-row'
    )}>
      {/* Enhanced Avatar */}
      <div className="flex-shrink-0">
        <div className={cn(
          'relative w-8 h-8 rounded-lg flex items-center justify-center shadow-sm',
          isUser 
            ? 'bg-gradient-to-br from-primary-600 to-primary-700' 
            : 'bg-gradient-to-br from-secondary-100 to-secondary-200 border border-secondary-300/50'
        )}>
          {isUser ? (
            <User className="w-4 h-4 text-white" />
          ) : (
            <Bot className="w-4 h-4 text-secondary-600" />
          )}
          {!isUser && (
            <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-gradient-to-br from-success-400 to-success-500 rounded-full flex items-center justify-center">
              <Sparkles className="w-1.5 h-1.5 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex-1 max-w-[82%] space-y-2',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div className={cn(
          'relative rounded-xl px-4 py-3 shadow-sm border backdrop-blur-sm text-[13px]',
          isUser
            ? 'bg-gradient-to-br from-primary-600 to-primary-700 text-white ml-auto border-primary-500/20'
            : 'bg-white/90 border-secondary-200/50 text-secondary-900'
        )}>
          {/* Message bubble tail */}
          <div className={cn(
            'absolute top-3 w-2.5 h-2.5 rotate-45',
            isUser 
              ? '-right-1.5 bg-gradient-to-br from-primary-600 to-primary-700'
              : '-left-1.5 bg-white/90 border-l border-b border-secondary-200/50'
          )}></div>

          <div className={cn(
            'prose prose-sm max-w-none text-[13px]',
            isUser 
              ? 'prose-invert prose-headings:text-white prose-p:text-white/95 prose-strong:text-white prose-code:text-primary-100' 
              : 'prose-secondary prose-headings:text-secondary-900 prose-p:text-secondary-800'
          )}>
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom styling for markdown elements
                p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                code: ({ children, className }) => (
                  <code className={cn(
                    'px-2 py-1 rounded-md text-xs font-mono font-medium',
                    isUser 
                      ? 'bg-primary-800/50 text-primary-100 border border-primary-500/30' 
                      : 'bg-secondary-100 text-secondary-800 border border-secondary-200',
                    className
                  )}>
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className={cn(
                    'p-3 rounded-lg text-[11px] font-mono overflow-x-auto border',
                    isUser 
                      ? 'bg-primary-800/50 text-primary-100 border-primary-500/30' 
                      : 'bg-secondary-50 text-secondary-800 border-secondary-200'
                  )}>
                    {children}
                  </pre>
                ),
                h1: ({ children }) => <h1 className="text-base font-bold mb-1.5">{children}</h1>,
                h2: ({ children }) => <h2 className="text-sm font-semibold mb-1.5">{children}</h2>,
                h3: ({ children }) => <h3 className="text-xs font-semibold mb-1">{children}</h3>,
                ul: ({ children }) => <ul className="space-y-1 ml-4">{children}</ul>,
                ol: ({ children }) => <ol className="space-y-1 ml-4">{children}</ol>,
                li: ({ children }) => <li className="text-[13px]">{children}</li>,
              }}
            >
              {showSuggestions ? body : message.text}
            </ReactMarkdown>
          </div>
        </div>

        {isUser && message.selectedFilters && message.selectedFilters.length > 0 && (
          <div className="flex max-w-full flex-wrap justify-end gap-1 px-1">
            {message.selectedFilters.map((filter) => (
              <span
                key={`${filter.type}-${filter.value}`}
                className="rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700"
              >
                {filter.type}: {filter.value}
              </span>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <div className={cn(
          'text-[11px] text-secondary-500 px-2 font-medium',
          isUser ? 'text-right' : 'text-left'
        )}>
          {formatDate(message.timestamp, language === 'am' ? 'am-ET' : 'en-US')}
        </div>

        {!isUser && onFeedback && (
          <div className="flex items-center gap-1 px-1 text-[11px] text-secondary-500">
            <span className="mr-1">Was this response helpful?</span>
            <button
              type="button"
              onClick={() => submitFeedback('helpful')}
              disabled={isSubmittingFeedback}
              aria-label="Mark response as helpful"
              title="Helpful"
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:cursor-wait disabled:opacity-60',
                feedbackRating === 'helpful'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'hover:bg-emerald-50 hover:text-emerald-700',
              )}
            >
              <ThumbsUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => submitFeedback('not_helpful')}
              disabled={isSubmittingFeedback}
              aria-label="Mark response as not helpful"
              title="Not helpful"
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:cursor-wait disabled:opacity-60',
                feedbackRating === 'not_helpful'
                  ? 'bg-red-100 text-red-700'
                  : 'hover:bg-red-50 hover:text-red-700',
              )}
            >
              <ThumbsDown className="h-3.5 w-3.5" />
            </button>
            {feedbackRating && <span className="ml-1 text-emerald-700">Thanks for your feedback.</span>}
          </div>
        )}

        {/* Clarification quick replies */}
        {showClarifications && (
          <div className="mr-auto mt-2 space-y-1.5">
            <p className="px-1 text-[10px] font-semibold uppercase tracking-wide text-cyan-700">
              {t("chat.clarification.choose")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {clarificationOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => onSuggestionClick?.(option)}
                  className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-medium text-cyan-800 shadow-sm transition-colors hover:border-cyan-300 hover:bg-cyan-100"
                  disabled={!onSuggestionClick}
                  title="Reply with this option"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Suggested queries */}
        {showSuggestions && (
          <div className={cn(
            'mt-2 flex flex-wrap gap-1.5',
            isUser ? 'ml-auto justify-end' : 'mr-auto'
          )}>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => onSuggestionClick?.(suggestion)}
                className="rounded-full border border-secondary-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-secondary-700 shadow-sm hover:border-primary-200 hover:text-primary-700 transition-colors"
                disabled={!onSuggestionClick}
                title="Use this query"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* Additional content (charts, tables, etc.) */}
        {children && (
          <div className={cn(
            'mt-3 space-y-3',
            isUser ? 'ml-auto' : 'mr-auto'
          )}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
};
